import { Box, Typography, Button, IconButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import withBoxStyle from '../../../HOC/layout/withBoxStyle';
import { CloseIcon } from '../../../components/shared/CustomIcons';
import imgLow from '/images/feeling-low@2x.png';
import imgSomewhatIll from '/images/feeling-somewhat-ill@2x.png';
import imgAlright from '/images/feeling-alright@2x.png';
import imgGreat from '/images/feeling-great@2x.png';
import useFetchWeeklySurvey from '../../../hooks/queries/useFetchWeeklySurvey';
import { useAppSelector } from '../../../redux/hooks';

interface MoodData {
  id: number | null;
  date: string;
  day: string;
  mood: string | null;
  isFutureDate: boolean;
}

const generateWeekMoodData = (
  moodData: { id: number | null; date: string; mood: string | null }[]
): MoodData[] => {
  const daysOfWeek = ['S', 'M', 'T', 'W', 'T', 'F', 'S']; // First letters for each day of the week
  const today = new Date(); // Get today's date
  const weekDates: MoodData[] = [];

  moodData.forEach((entry) => {
    const currentDate = new Date(entry.date); // Convert the date string to a Date object
    const dayOfWeek = daysOfWeek[currentDate.getDay()]; // Get first letter of the day of the week
    const dayNumber = currentDate.getDate(); // Get the day of the month as a number (e.g., 9, 10, etc.)

    // Check if the date is in the future
    const isFutureDate = currentDate > today;

    weekDates.push({
      id: entry.id,
      date: dayNumber.toString(), // Use only the day number
      day: dayOfWeek, // First letter of the day (M, T, W, etc.)
      mood: entry.mood,
      isFutureDate, // Flag if the date is in the future
    });
  });

  return weekDates;
};

const WeeklyFeelingSurveyTracker = withBoxStyle(() => {
  const patientId = useAppSelector((state) => state.auth.currentUser?.id);

  const { mobile_number = '', country_code = '' } =
    useAppSelector((state) => state.auth?.constants) || {};

  const { weekData } = useFetchWeeklySurvey(patientId);

  const result = generateWeekMoodData(weekData);

  function getCurrentWeekDatesAndRange() {
    const today = new Date();
    const monthsOfYear = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];

    // Get the current day (0 = Sunday, 1 = Monday, etc.)
    const currentDay = today.getDay();

    // Calculate how many days to subtract to get Monday (day 1)
    const diffToMonday = currentDay === 0 ? 6 : currentDay - 1;

    // Get the start date (Monday)
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - diffToMonday);

    const weekDatesArray = [];

    // Loop through the week from Monday to Sunday
    for (let i = 0; i < 7; i++) {
      const currentDate = new Date(startOfWeek);
      currentDate.setDate(startOfWeek.getDate() + i);
      weekDatesArray.push(currentDate.getDate()); // Add the day (date) to the array
    }

    // Formatting the start and end dates for the week range
    const startDay = startOfWeek.getDate();
    const startMonth = monthsOfYear[startOfWeek.getMonth()];

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    const endDay = endOfWeek.getDate();
    const endMonth = monthsOfYear[endOfWeek.getMonth()];

    const formattedWeekRange = `${startDay} ${startMonth} - ${endDay} ${endMonth}`;

    return {
      weekDatesArray,
      weekRange: formattedWeekRange,
    };
  }

  const { weekRange } = getCurrentWeekDatesAndRange();

  const navigate = useNavigate();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w: any = {
    low: {
      img: imgLow,
      alt: 'Feeling low',
    },
    alright: {
      img: imgAlright,
      alt: 'Feeling alright',
    },
    somewhat_ill: {
      img: imgSomewhatIll,
      alt: 'Feeling somewhat ill',
    },
    great: {
      img: imgGreat,
      alt: 'Feeling great',
    },
  };

  return (
    <Box className="d-flex flex-column flex-1">
      <Box sx={{ pt: 8, px: 3, pb: 3 }} className="d-flex flex-column">
        <IconButton
          aria-label="back"
          sx={{ color: 'common.white', p: 0, mb: 4.5, alignSelf: 'end' }}
          onClick={() => {
            navigate('/dashboard');
          }}
        >
          <CloseIcon sx={{ fontSize: 40, color: 'text.primary' }} />
        </IconButton>
        <Typography variant="h4" sx={{ mb: 1 }}>
          Your week
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.tertiary', mb: 5 }}>
          Keep track of how you've been feeling
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {`${weekRange}`}
        </Typography>
      </Box>
      <Box sx={{ gap: '1px' }} className="d-flex flex-column flex-1">
        <Box
          sx={{ bgcolor: 'common.white', px: 3, py: 5.25 }}
          className="rounded-50"
        >
          <Box
            sx={{ gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}
            className="d-grid"
          >
            {result?.map((day, index) => (
              <Box
                key={index}
                sx={{ gap: 3 }}
                className="d-flex flex-column align-items-center"
              >
                <Typography variant="body2">{day.day}</Typography>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    ...{
                      border: !day?.mood
                        ? day?.isFutureDate
                          ? '1.5px dashed #7625dc'
                          : '1.5px solid #47546733'
                        : 'none',
                    },
                  }}
                  className="d-flex align-items-center justify-content-center rounded-circle"
                >
                  {!!day?.mood && (
                    <img
                      src={w[day?.mood]?.img}
                      alt={`Feeling Low`}
                      width="36px"
                    />
                  )}
                </Box>
                <Typography variant="body1" sx={{ color: 'tertiary.main' }}>
                  {day.date}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
        <Box
          sx={{ bgcolor: 'common.white', px: 3, py: 4 }}
          className="rounded-50"
        >
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 3 }}>
            Are you experiencing any issues with your eye health?
          </Typography>
          <Button
            variant="contained"
            color="tertiary"
            fullWidth
            size="small"
            type="submit"
            sx={{ fontWeight: 400 }}
            href={`tel:${country_code}${mobile_number}`}
          >
            Contact us
          </Button>
        </Box>
      </Box>
    </Box>
  );
});

export default WeeklyFeelingSurveyTracker;
